import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/auth-context';
import { WorkspaceService, UserWorkspace } from '@/services/workspace-service.ts';
import { getWorkspaceUrlFromObject, isCentralDomain } from '@/utils/tenant-utils';

interface UseDefaultWorkspaceReturn {
  defaultWorkspace: UserWorkspace | null;
  userWorkspaces: UserWorkspace[];
  shouldRedirectToDefault: boolean;
  loading: boolean;
  redirectToDefaultWorkspace: () => void;
  refreshWorkspaces: () => Promise<void>;
}

export const useDefaultWorkspace = (): UseDefaultWorkspaceReturn => {
  const { user, loading: authLoading } = useAuth();
  const [defaultWorkspace, setDefaultWorkspace] = useState<UserWorkspace | null>(null);
  const [userWorkspaces, setUserWorkspaces] = useState<UserWorkspace[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasRedirected, setHasRedirected] = useState(false);

  const loadWorkspaces = async () => {
    if (!user || authLoading) {
      setDefaultWorkspace(null);
      setUserWorkspaces([]);
      setHasRedirected(false);
      return;
    }

    setLoading(true);

    try {
      // Load all user workspaces
      const workspaces = await WorkspaceService.getUserWorkspaces();
      setUserWorkspaces(workspaces);

      // Get default workspace
      const defaultWs = await WorkspaceService.getDefaultWorkspace();
      setDefaultWorkspace(defaultWs);

    } catch (error) {
      console.error('Error loading workspaces:', error);
      setDefaultWorkspace(null);
      setUserWorkspaces([]);
    } finally {
      setLoading(false);
    }
  };

  const refreshWorkspaces = async () => {
    console.log('🔄 Refreshing workspace data...');
    await loadWorkspaces();
  };

  useEffect(() => {
    loadWorkspaces();
  }, [user, authLoading]);

  // Determine if user should be redirected to default workspace
  const isOnAuthPages = typeof window !== 'undefined' && 
    (window.location.pathname.startsWith('/auth') || 
     window.location.pathname.startsWith('/invite'));
  
  const shouldRedirectToDefault = user && 
    !authLoading &&
    !loading && 
    defaultWorkspace &&
    isCentralDomain() && 
    !isOnAuthPages &&
    !hasRedirected &&
    (window.location.pathname === '/workspaces' || 
     (window.location.pathname === '/' && 
      !window.location.search.includes('no-redirect') &&
      !window.location.search.includes('tenant=') &&
      !window.location.search.includes('returnUrl=')));

  const redirectToDefaultWorkspace = () => {
    if (defaultWorkspace && !hasRedirected) {
      setHasRedirected(true);
      const workspaceUrl = getWorkspaceUrlFromObject(defaultWorkspace);
      
      // Small delay to show the visual notification
      setTimeout(() => {
        window.location.href = workspaceUrl;
      }, 500);
    }
  };

  return {
    defaultWorkspace,
    userWorkspaces,
    loading,
    shouldRedirectToDefault,
    redirectToDefaultWorkspace,
    refreshWorkspaces
  };
}; 